
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Calculator, Zap, Factory, Recycle, Flame, Target, Menu } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { EnergyCalculator } from './EnergyCalculator';
import { WasteCalculator } from './WasteCalculator';
import { ChemicalCalculator } from './ChemicalCalculator';
import { ProductionCalculator } from './ProductionCalculator';
import { CarbonSummary } from './CarbonSummary';

export const CarbonAccountingMain = () => {
  const [refreshData, setRefreshData] = useState(0);
  const [activeTab, setActiveTab] = useState('energy');

  const tabs = [
    { value: 'energy', label: 'Energy', icon: Zap },
    { value: 'waste', label: 'Waste', icon: Recycle },
    { value: 'chemical', label: 'Chemical', icon: Flame },
    { value: 'production', label: 'Production', icon: Factory },
  ];

  const activeTabLabel = tabs.find(t => t.value === activeTab)?.label || 'Energy';

  const handleCalculationUpdate = () => {
    setRefreshData(prev => prev + 1);
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calculator className="h-5 w-5" />
            Carbon Accounting & Emission Calculators
          </CardTitle>
          <p className="text-gray-600">
            Comprehensive carbon accounting with multi-currency support and automated conversions
          </p>
        </CardHeader>
        <CardContent>
          {/* Summary Dashboard */}
          <CarbonSummary key={refreshData} />

          {/* Calculator Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-6 space-y-6">
            {/* Desktop Tabs List */}
            <TabsList className="hidden md:grid w-full grid-cols-4">
              {tabs.map((tab) => (
                <TabsTrigger key={tab.value} value={tab.value} className="flex items-center gap-2">
                  <tab.icon className="h-4 w-4" />
                  {tab.label}
                </TabsTrigger>
              ))}
            </TabsList>

            {/* Mobile Tabs Dropdown */}
            <div className="md:hidden">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="w-full flex justify-start items-center gap-3 px-4 py-6">
                    <Menu className="h-5 w-5 text-gray-500" />
                    <span className="flex items-center gap-2 border-l pl-3">
                      {React.createElement(tabs.find(t => t.value === activeTab)?.icon || Zap, { className: "h-5 w-5" })}
                      <span className="font-semibold">{activeTabLabel}</span>
                    </span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-64" align="start">
                  {tabs.map((tab) => (
                    <DropdownMenuItem
                      key={tab.value}
                      onClick={() => setActiveTab(tab.value)}
                      className="flex items-center gap-2 py-3"
                    >
                      <tab.icon className="h-4 w-4" />
                      {tab.label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            <TabsContent value="energy">
              <EnergyCalculator onUpdate={handleCalculationUpdate} />
            </TabsContent>

            <TabsContent value="waste">
              <WasteCalculator onUpdate={handleCalculationUpdate} />
            </TabsContent>

            <TabsContent value="chemical">
              <ChemicalCalculator onUpdate={handleCalculationUpdate} />
            </TabsContent>

            <TabsContent value="production">
              <ProductionCalculator onUpdate={handleCalculationUpdate} />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};
